// src/components/wellfinder/adjudicationKeys.js
//
// THE KEYMAP, AS DATA. The handler and the `?` overlay both read this, so the
// help can never describe a binding that does not exist — same reasoning as the
// legend and the map paint.
//
// ── WHY THESE KEYS ───────────────────────────────────────────────────────────
//
// The budget is three seconds for 9,348 tier A and A′ records. That is not a
// target to aim at, it is a constraint that decides the design: one keystroke
// has to carry the whole common decision, or tier A alone goes from five hours
// to a day.
//
// So the commit key also carries equipment_type. It is the one fact only the
// imagery provides — the registry has no equipment concept at all — and asking
// for it as a second keystroke doubles the common path. TWO COMMIT KEYS rather
// than a modifier: `p` and `f` are each one unmodified keypress, where
// `a`+shift is two, and the mnemonic is exact (pump jack / frac well) instead
// of arbitrary. A modifier would also have made the two equipment types feel
// like a variant of one action when they are simply the two answers.
//
// `a` exists for the case the imagery does not settle — approve the detection
// and the registry match without asserting equipment. It writes no
// equipment_type at all rather than guessing one, because a wrong
// provenance-stamped 'analyst' value is worse than an absent one.
//
// Home row and left hand: p, f, a, r, x, n, m, u all sit under the left hand or
// near it, so the right hand can stay on the number row for candidate
// selection. Nothing needs a modifier and nothing needs two hands.
//
// Reject is deliberately NOT one key. The server 400s without a reason, six
// reasons are a real choice, and reject is not on the three-second path — it is
// a Tier C decision at five minutes. `r` opens the picker; a digit chooses.
//
// NO CONFIRMATION DIALOGS ANYWHERE. At three seconds a dialog doubles the
// interaction. The undo toast is the safety net instead: reopen withdraws the
// verdict, keeps the work, and leaves an event, which is a better answer than a
// modal because it is honest about the record having been touched.

export const KEY_ACTIONS = Object.freeze([
  {
    keys: ["p"],
    id: "approve_pump_jack",
    label: "Approve · pump jack",
    // WHAT AN APPROVE ADOPTS (2026-10-25). Not rank 1 since 5d5aa439: the
    // selected candidate, else the record's own match (tiers A and A′ arrive
    // matched). Candidates and neither, it asks rather than guessing.
    detail: "Approves with the selected candidate, or the record's own match; sets equipment pump_jack; advances. Candidates and neither: asks. After 0: Approved – No Candidate, pump_jack",
    group: "Commit",
    primary: true,
  },
  {
    keys: ["f"],
    id: "approve_frac_well",
    label: "Approve · frac well",
    detail: "Approves with the selected candidate, or the record's own match; sets equipment frac_well; advances. Candidates and neither: asks. After 0: Approved – No Candidate, frac_well",
    group: "Commit",
    primary: true,
  },
  {
    keys: ["a"],
    id: "approve_no_equipment",
    label: "Approve · equipment unclear",
    detail: "Approves with the selected candidate, or the record's own match; writes no equipment_type; advances. Candidates and neither: asks. After 0: Approved – No Candidate, no equipment_type",
    group: "Commit",
  },
  {
    // APPROVED – NO CANDIDATE (DB 2026-10-25). The well is real and none of the
    // candidates is it. 0 because it is "none of 1–9", and it sits beside them.
    // Approves with no registry match; the server refuses it on a record with no
    // candidates, so tier C keeps a plain approve.
    //
    // 0 ARMS, THEN p, f OR a COMMITS (2026-10-25). No Candidate states its
    // equipment as a normal approve does, so the commit keys stay the ones that
    // say it: two plain keypresses. Shift+p/f/a would also be two, and Caps
    // Lock would turn every normal approve into a declined match.
    keys: ["0"],
    id: "approve_no_candidate",
    label: "Approved – No Candidate",
    detail: "Arms Approved – No Candidate (none of the candidates is this well); then p, f or a states the equipment and approves with no registry match. 0 again or Esc cancels; not on a record with no candidates",
    group: "Commit",
  },
  {
    keys: ["r"],
    id: "reject",
    label: "Reject",
    detail: "Opens the reason picker — a reason is required",
    group: "Commit",
  },
  {
    keys: ["x"],
    id: "flag",
    label: "Flag for review",
    detail: "Leaves your queue, stays under the status filter",
    group: "Commit",
  },
  {
    keys: ["1", "…", "9"],
    id: "select_candidate",
    label: "Select candidate by rank",
    detail: "Becomes the adopt target",
    group: "Record",
  },
  {
    keys: ["z"],
    id: "zoom",
    label: "Toggle detail zoom",
    detail: "Between context (z18) and detail (z20). A record opens at whichever suits it; this flips",
    group: "Record",
  },
  { keys: ["n"], id: "note", label: "Add a note", detail: "Optional on any verdict", group: "Record" },
  { keys: ["m"], id: "move", label: "Move the point", detail: "Explicit mode; drag the marker", group: "Record" },
  { keys: ["b"], id: "boundary", label: "Edit the boundary", detail: "Explicit mode; drag the corners", group: "Record" },
  { keys: ["u"], id: "undo", label: "Undo last commit", detail: "Reopens it, keeping the values", group: "Record" },
  { keys: ["j", "↓"], id: "next", label: "Next record", group: "Navigate" },
  { keys: ["k", "↑"], id: "prev", label: "Previous record", group: "Navigate" },
  { keys: ["Esc"], id: "cancel", label: "Cancel the current mode", group: "Navigate" },
  { keys: ["?"], id: "help", label: "This list", group: "Navigate" },
]);

export const KEY_GROUPS = ["Commit", "Record", "Navigate"];

/**
 * Resolve a KeyboardEvent to an action id, or null.
 *
 * NEVER FIRES WHILE A TEXT INPUT HAS FOCUS. The note field is a textarea and
 * every letter here is a destructive command — typing "approve pumpjack" into a
 * note would commit five records. contentEditable is included because a rich
 * text field would not report as INPUT.
 */
export function resolveKey(e) {
  const t = e.target;
  const tag = t?.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable) {
    // Esc still works, so a text field is never a trap.
    return e.key === "Escape" ? "cancel" : null;
  }
  // Modifiers are the browser's and the OS's. Ctrl+R must stay reload.
  if (e.ctrlKey || e.metaKey || e.altKey) return null;

  switch (e.key) {
    case "p": return "approve_pump_jack";
    case "f": return "approve_frac_well";
    case "a": return "approve_no_equipment";
    case "0": return "approve_no_candidate";
    case "r": return "reject";
    case "x": return "flag";
    case "z": return "zoom";
    case "n": return "note";
    case "m": return "move";
    case "b": return "boundary";
    case "u": return "undo";
    case "j": case "ArrowDown": return "next";
    case "k": case "ArrowUp": return "prev";
    case "Escape": return "cancel";
    case "?": return "help";
    default:
      if (/^[1-9]$/.test(e.key)) return `candidate:${e.key}`;
      return null;
  }
}

/** The equipment_type each commit key asserts, or null for "do not assert". */
export const EQUIPMENT_FOR_ACTION = Object.freeze({
  approve_pump_jack: "pump_jack",
  approve_frac_well: "frac_well",
  approve_no_equipment: null,
});
